import type { AppointmentFormValues } from '@/types/appointment-form';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import type { OrgLocation } from '@/auth/auth-api';

/**
 * Build the `CreateAppointmentPanel` pre-fill from a Fulfilled `DayListRow`
 * for the calendar row-action `'follow-up'` flow (PROD-919). Mirrors
 * Flutter `calendar_tab.dart:2306-2307` (`bookFollowUpAppointment` →
 * `widget.onSelect!(appointment, context, false, true)`).
 *
 * Caller passes:
 *   - `row` — the source appointment row (must be Fulfilled + not
 *     recurring per `getRowActionsFor('fulfilled')` upstream gate).
 *   - `firstLocation` — `useAuthStore.getState().user.organizationLocations[0]`
 *     when present AND has a real GUID, else null. Mirrors Flutter
 *     `calendar_screen.dart:180`. We refuse `{id:""}` truthy-but-empty
 *     because the LocationSelector's `if (!selected)` guard would skip
 *     auto-pick and the user would land on a non-pickable empty Guid
 *     that 400s on Save.
 *   - `selectedDateISO` — `YYYY-MM-DD` from the calendar's currently
 *     selected day. Used to seed the date strip on the panel; the user
 *     can pick a future day for the follow-up itself.
 *
 * Returns the partial form values shape. Caller hands the result to
 * `setPendingInitialValues` and opens the panel via `setActivePanel('appointment')`.
 *
 * Important: `referenceAppointmentId = row.id` is the signal that locks
 * the profile picker to FOLLOWUP-type profiles only (see the comment in
 * `frame-scheduling.tsx` `handleFuCreateFollowUp` for the parity note —
 * React collapses Flutter's `selectedAppointmentType` string + the
 * Coding-object `appointmentType` into this one signal).
 */
export function buildFollowUpInitialFromRow(
  row: DayListRow,
  firstLocation: OrgLocation | null,
  selectedDateISO: string,
): Partial<AppointmentFormValues> {
  // Empty-GUID refusal — explicit runtime guard (Samer review on PR #343
  // §2 — "make sure this throws/returns null visibly rather than
  // silently falling through"). The caller is expected to pre-filter
  // (`userLocations[0]?.id ? userLocations[0] : null`); if a truthy-but-
  // empty location still arrives, drop it to null here so the
  // LocationSelector's `if (!selected)` auto-pick guard triggers
  // instead of landing the user on a non-pickable empty-GUID location
  // that 400s on Save. Logging makes the refusal visible during dev /
  // QA rather than silently masking a caller-side bug.
  const locationHasRealId =
    !!firstLocation && typeof firstLocation.id === 'string' && firstLocation.id.length > 0;
  if (firstLocation && !locationHasRealId) {
    // eslint-disable-next-line no-console
    console.warn(
      '[build-follow-up-from-row] firstLocation passed without a real id; refusing to seed an empty-GUID location. Caller is expected to pre-filter.',
      firstLocation,
    );
  }
  return {
    location: locationHasRealId
      ? {
          id: firstLocation!.id,
          name: firstLocation!.name ?? '',
        }
      : null,
    providerId: row.practitionerId ?? null,
    providerName: row.doctorName,
    providerSpeciality: row.doctorSpecialty ?? null,
    patientId: row.patientId ?? null,
    patientName: row.patientName,
    patientPhoto: row.patientAvatar ?? null,
    patientDob: row.dob ?? null,
    // Carry the source RFV forward as the seed for the new appointment.
    // The picker will overwrite if the user picks a profile that bundles
    // its own reasonCode. (Flutter `calendar_screen.dart:170` does the
    // same.) `code` + `definition` are blank because DayListRow only
    // carries the display string — that's fine, the picker fills them
    // in when the user re-selects an RFV from the dropdown.
    reasonCode: row.reasonForVisit
      ? {
          code: '',
          display: row.reasonForVisit,
          definition: '',
        }
      : null,
    // ----------------------------------------------------------------
    // FOLLOWUP-LOCK CONTRACT — do NOT remove or rename this field
    // without auditing `appointment-profile-section.tsx` profile-filter.
    //
    // `referenceAppointmentId` non-null is THE signal that locks the
    // profile picker to FOLLOWUP-type profiles only. The section reads
    // `watch('referenceAppointmentId')` and filters
    // `allProfiles.filter(p => p.appointmentType.code === 'FOLLOWUP')`
    // when set. Clearing or renaming this field without updating the
    // filter would show ALL profile types (First Appointment, General,
    // Walk-in, etc.) in the FU Create Follow Up flow — wrong UX +
    // breaks the schema's FOLLOWUP-requires-referenceAppointmentId
    // rule at `appointment-form.ts:357-364`.
    //
    // React collapses Flutter's two-field pattern
    // (`selectedAppointmentType` string lock + `appointmentType`
    // Coding) into this single signal — see PROD-920 PR #328 commit
    // for the full Flutter parity note. Samer review on PR #343 §4
    // asked for this to be documented inline (was only in PR body).
    // ----------------------------------------------------------------
    referenceAppointmentId: row.id,
    selectedDate: selectedDateISO,
  };
}
